import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Shield, Swords, Trophy, Users, Loader2 } from "lucide-react";

export default function Club() {
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [activeClub, setActiveClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", location: "Charlotte, NC" });

  const loadClubs = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/clubs");
      setClubs(data);
      if (data[0]) selectClub(data[0].club_id);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };

  const selectClub = async (id) => {
    try {
      const { data } = await http.get(`/clubs/${id}`);
      setActiveClub(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadClubs(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Give your club a name"); return; }
    try {
      const { data } = await http.post("/clubs", form);
      toast.success(`Club "${data.name}" created — invite your dinkers.`);
      setCreateOpen(false);
      setForm({ name: "", description: "", location: "Charlotte, NC" });
      await loadClubs();
      selectClub(data.club_id);
    } catch (e) { toast.error(formatError(e)); }
  };

  const joinClub = async (c) => {
    try {
      await http.post(`/clubs/${c.club_id}/join`);
      toast.success(`Joined ${c.name}. Welcome to the ladder.`);
      await loadClubs();
      selectClub(c.club_id);
    } catch (e) { toast.error(formatError(e)); }
  };

  const leaveClub = async () => {
    try {
      await http.post(`/clubs/${activeClub.club_id}/leave`);
      toast.success("Left the club.");
      loadClubs();
    } catch (e) { toast.error(formatError(e)); }
  };

  const isMember = activeClub?.members?.includes(user?.user_id);

  const challengeUp = async (target) => {
    try {
      await http.post("/clubs/ladder/challenge", { club_id: activeClub.club_id, target_user_id: target.user_id });
      toast.success(`Ladder challenge sent to ${target.name}.`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const simulateWin = async (target) => {
    // demo helper - main user wins the rung
    try {
      await http.post(`/clubs/ladder/resolve?club_id=${activeClub.club_id}&winner_id=${user.user_id}&loser_id=${target.user_id}`);
      toast.success(`You climbed past ${target.name}.`);
      selectClub(activeClub.club_id);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="club-page">
      <section className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="pill pv-bg-highlight text-black mb-2">CLUB & LADDER</div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold pv-text-dark">Climb the ladder. Rule your court.</h1>
        </div>
        <button data-testid="create-club-btn" onClick={()=>setCreateOpen(true)} className="btn-primary flex items-center gap-2"><Plus size={16}/> New club</button>
      </section>

      {loading ? <Loader2 className="animate-spin"/> : (
        <div className="grid lg:grid-cols-4 gap-5">
          <aside className="lg:col-span-1 space-y-3">
            {clubs.map((c) => (
              <button
                key={c.club_id}
                data-testid={`club-item-${c.club_id}`}
                onClick={()=>selectClub(c.club_id)}
                className={`w-full text-left card p-4 transition-all ${activeClub?.club_id===c.club_id?"ring-2 ring-[#BED445]":""}`}
              >
                <div className="flex items-center gap-2">
                  <Shield size={16} className="pv-text-support"/>
                  <div className="font-bold pv-text-dark">{c.name}</div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{c.location} · {c.member_count} members</div>
              </button>
            ))}
          </aside>

          {activeClub && (
            <section className="lg:col-span-3 space-y-5">
              <div className="card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-display font-extrabold pv-text-dark">{activeClub.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">{activeClub.description}</p>
                    <div className="text-xs text-gray-500 mt-1">{activeClub.location} · {activeClub.members?.length} members</div>
                  </div>
                  {isMember ? (
                    <button data-testid="leave-club-btn" onClick={leaveClub} className="btn-ghost text-sm">Leave</button>
                  ) : (
                    <button data-testid={`join-club-btn-${activeClub.club_id}`} onClick={()=>joinClub(activeClub)} className="btn-primary text-sm">Join club</button>
                  )}
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="pv-bg-deep-green text-white px-4 py-3 rounded-t-xl flex items-center justify-between font-display font-bold">
                  <span className="flex items-center gap-2"><Trophy size={16}/> Ladder Standings</span>
                  <span className="text-xs font-normal opacity-80">Challenge up to 2 spots</span>
                </div>
                <div className="divide-y">
                  {(activeClub.ladder_full || []).map(({position, user: u}) => {
                    if (!u) return null;
                    const isMe = u.user_id === user?.user_id;
                    const myPos = activeClub.ladder_full?.find(l => l.user?.user_id === user?.user_id)?.position;
                    const canChallenge = isMember && myPos && !isMe && (myPos - position) >= 1 && (myPos - position) <= 2;
                    return (
                      <div key={u.user_id} className={`px-4 py-3 flex items-center gap-3 ${isMe?"bg-[#F8F6F3]":""}`} data-testid={`ladder-row-${position}`}>
                        <div className="w-9 h-9 rounded-full pv-bg-dark grid place-items-center font-extrabold pv-text-primary">{position}</div>
                        <Avatar className="h-9 w-9"><AvatarImage src={u.avatar}/><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                        <div className="flex-1">
                          <div className="font-bold pv-text-dark flex items-center gap-1">{u.name} {isMe && <span className="pill pv-bg-primary text-black text-[10px]">You</span>}</div>
                          <div className="text-xs text-gray-500">DUPR {u.dupr_rating} · {u.wins||0}W - {u.losses||0}L</div>
                        </div>
                        {canChallenge && (
                          <div className="flex gap-2">
                            <button data-testid={`ladder-challenge-${u.user_id}`} onClick={()=>challengeUp(u)} className="btn-challenge text-xs flex items-center gap-1"><Swords size={12}/> Challenge</button>
                            <button data-testid={`ladder-win-${u.user_id}`} onClick={()=>simulateWin(u)} className="btn-ghost text-xs">Won it</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="card p-5">
                <h3 className="font-display font-bold text-lg pv-text-dark flex items-center gap-2"><Users size={18}/> Roster</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(activeClub.roster || []).map((r) => (
                    <div key={r.user_id} className="flex items-center gap-2 bg-[#F8F6F3] rounded-full pr-3 pl-1 py-1 border">
                      <Avatar className="h-6 w-6"><AvatarImage src={r.avatar}/><AvatarFallback>{r.name?.[0]}</AvatarFallback></Avatar>
                      <span className="text-xs font-bold">{r.name}</span>
                      <span className="text-[10px] pill pv-bg-primary text-black">{r.dupr_rating}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-club-dialog">
          <DialogHeader><DialogTitle>Start a new club</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="club-name-input" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} placeholder="Queen City Dinkers"/></div>
            <div><Label>Description</Label><Input data-testid="club-desc-input" value={form.description} onChange={(e)=>setForm(f=>({...f,description:e.target.value}))} placeholder="4.0+ competitive league"/></div>
            <div><Label>Location</Label><Input data-testid="club-loc-input" value={form.location} onChange={(e)=>setForm(f=>({...f,location:e.target.value}))}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)}>Cancel</Button>
            <button data-testid="submit-club-btn" onClick={create} className="btn-primary">Create club</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
